/**
 * VaultTeamPanel — leadership team cards in Secure Vault (Team tab).
 * Seeded with Robert (Founder & CEO), Atlas (CTO) and Rebecca; founder can
 * add, edit and remove members.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Plus, Trash2, Pencil, X, Check, Users, Camera, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: '', title: '', photo_url: '', bio: '' };

const photoSrc = (url) => (url && url.startsWith('/') ? `${API_URL}${url}` : url);

const Avatar = ({ m }) => m.photo_url ? (
  <img src={photoSrc(m.photo_url)} alt={m.name} className="w-24 h-24 rounded-2xl object-cover" style={{ border: '2px solid #E5E7EB' }} />
) : (
  <div className="w-24 h-24 rounded-2xl flex items-center justify-center text-2xl font-black text-white"
    style={{ background: 'linear-gradient(135deg, #F5831F 0%, #C2410C 100%)' }}>
    {(m.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('')}
  </div>
);

export const VaultTeamPanel = () => {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(null); // null | {mode:'add'|'edit', member_id?, ...fields}
  const [uploadingId, setUploadingId] = useState(null);
  const fileRef = useRef(null);
  const uploadTargetRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await apiClient.request('/api/vault/team');
      setMembers(d.members || []);
    } catch (e) { toast.error(e?.message || 'Failed to load team'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim() || !form.title.trim()) { toast.error('Name and title are required'); return; }
    const body = JSON.stringify({ name: form.name, title: form.title, photo_url: form.photo_url, bio: form.bio });
    try {
      if (form.mode === 'edit') {
        await apiClient.request(`/api/vault/team/${form.member_id}`, { method: 'PUT', body });
      } else {
        await apiClient.request('/api/vault/team', { method: 'POST', body });
      }
      toast.success('Team updated');
      setForm(null);
      load();
    } catch (e) { toast.error(e?.message || 'Save failed'); }
  };

  const remove = async (id) => {
    try { await apiClient.request(`/api/vault/team/${id}`, { method: 'DELETE' }); load(); } catch (e) { toast.error('Delete failed'); }
  };

  const pickPhoto = (memberId) => {
    uploadTargetRef.current = memberId;
    fileRef.current?.click();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const memberId = uploadTargetRef.current;
    if (!file || !memberId) return;
    if (file.size > 4 * 1024 * 1024) { toast.error('Image too large (max 4 MB)'); return; }
    setUploadingId(memberId);
    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/vault/team/${memberId}/photo`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!r.ok) throw new Error((await r.json())?.detail || 'Upload failed');
      toast.success('Photo updated');
      load();
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    finally { setUploadingId(null); }
  };

  return (
    <div className="space-y-5" data-testid="vault-team-panel">
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadPhoto} data-testid="team-photo-file-input" />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-black flex items-center gap-2" style={{ color: '#111827' }}><Users size={18} /> Leadership Team</h2>
          <p className="text-xs text-gray-500">The people behind HiBFF — shown here for investors and data-room readiness.</p>
        </div>
        <button onClick={() => setForm({ mode: 'add', ...EMPTY })} data-testid="add-team-member-btn"
          className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5" style={{ backgroundColor: '#F5831F' }}>
          <Plus size={14} /> Add Member
        </button>
      </div>

      {form && (
        <div className="bg-white rounded-2xl p-5 space-y-3" style={{ border: '2px solid #F5831F40' }} data-testid="team-member-form">
          <div className="flex flex-wrap gap-2">
            <input placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm flex-1 min-w-[160px]" style={{ borderColor: '#E0E0E0' }} data-testid="team-name-input" />
            <input placeholder="Title (e.g. COO)" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm flex-1 min-w-[160px]" style={{ borderColor: '#E0E0E0' }} data-testid="team-title-input" />
          </div>
          <input placeholder="Photo URL (optional — leave blank for initials placeholder)" value={form.photo_url} onChange={e => setForm({ ...form, photo_url: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="team-photo-input" />
          <textarea placeholder="Short bio" rows={2} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="team-bio-input" />
          <div className="flex gap-2">
            <button onClick={save} className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5" style={{ backgroundColor: '#10B981' }} data-testid="team-save-btn"><Check size={14} /> Save</button>
            <button onClick={() => setForm(null)} className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5" style={{ backgroundColor: '#F3F4F6', color: '#374151' }}><X size={14} /> Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {members.map(m => (
          <div key={m.member_id} className="bg-white rounded-2xl p-5 flex flex-col gap-3" style={{ border: '1px solid #E5E7EB' }} data-testid={`team-card-${m.member_id}`}>
            <div className="flex items-start justify-between">
              <Avatar m={m} />
              <div className="flex gap-1">
                <button onClick={() => pickPhoto(m.member_id)} className="p-1.5 rounded hover:bg-orange-50" title="Upload photo" data-testid={`team-upload-${m.member_id}`}>
                  {uploadingId === m.member_id ? <Loader2 size={14} className="animate-spin" style={{ color: '#F5831F' }} /> : <Camera size={14} style={{ color: '#F5831F' }} />}
                </button>
                <button onClick={() => setForm({ mode: 'edit', ...m })} className="p-1.5 rounded hover:bg-gray-100" title="Edit name, title & bio" data-testid={`team-edit-${m.member_id}`}><Pencil size={14} style={{ color: '#6B7280' }} /></button>
                <button onClick={() => remove(m.member_id)} className="p-1.5 rounded hover:bg-red-50" title="Remove" data-testid={`team-delete-${m.member_id}`}><Trash2 size={14} style={{ color: '#EF4444' }} /></button>
              </div>
            </div>
            <div>
              <p className="font-black text-base" style={{ color: '#111827' }}>{m.name}</p>
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#F5831F' }}>{m.title}</p>
              {m.bio && <p className="text-xs mt-2" style={{ color: '#6B7280' }}>{m.bio}</p>}
              {!m.photo_url && <p className="text-[10px] mt-2 italic" style={{ color: '#9CA3AF' }}>No photo yet — tap the camera icon to upload one</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VaultTeamPanel;
